import { useMediaQuery, useTheme } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { Location } from 'history';
import queryString from 'query-string';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { PIPELINE_CARD_STAGE_ID } from 'containers/ApplicantsV2/constants';
import { State as ApplicantsV2State } from 'containers/ApplicantsV2/reducer';
import makeSelectApplicantsV2 from 'containers/ApplicantsV2/selectors';
import { setApplicantMessagesAction } from 'containers/ApplicantTableV2/actions';
import { DEFAULT_ROWS_PER_PAGE } from 'containers/ApplicantTableV2/constants';
import { buildApplicantsQueryParams } from 'containers/ApplicantTableV2/queryParams';
import {
  makeSelectApplicants,
  makeSelectHasFetchedApplicants,
  makeSelectIsFetchingApplicants,
} from 'containers/ApplicantTableV2/selectors';
import { ApplicantTableV2 } from 'containers/ApplicantTableV2/types';
import { messages as openingMessages } from 'containers/Openings/components/FilterableOpeningsTable/messages';
import { ApplicantDetailOutcome } from 'hooks/useApplicantDetail';
import { usePostHog } from 'hooks/usePostHog';
import { resolveProfileViewSource } from 'utils/applicantProfileView';
import { addApplicantTransitionedListener } from 'utils/appEvents';
import { history } from 'utils/history';
import {
  getApplicantTableScope,
  removeParamsFromUrl,
  setParamsToUrl,
  setParamsToUrlWithReplace,
} from 'utils/urlUtils';

import { isUniversalSearchOpen, loadQueue, LoadQueueInput } from './loadQueue';
import { messages } from './messages';

type EntryPoint = 'resume_link' | 'files_tab';
type ExitMethod = 'button' | 'escape' | 'drawer_close' | 'navigation';
export type NavigationMethod = 'button' | 'arrow_key' | 'auto_advance';
interface QueueSnapshot {
  ids: string[];
  truncated: boolean;
  label: string;
}
interface DetailSettlement {
  applicantId: string;
  outcome: ApplicantDetailOutcome;
}
interface ResumeReviewContextValue {
  isFullPage: boolean;
  enter: (entryPoint: EntryPoint) => void;
  exit: (exitMethod: ExitMethod) => void;
  hasEntered: boolean;
  useLegacyPagination: boolean;
  queue: (QueueSnapshot & { position: number }) | null;
  go: (direction: 'next' | 'previous', method: NavigationMethod) => void;
  isNavigating: boolean;
  navigationMethod: NavigationMethod | null;
  reviewSessionId: string | null;
  detailSettlement: DetailSettlement | null;
  reportDetailSettled: (
    applicantId: string,
    outcome: ApplicantDetailOutcome,
  ) => void;
}

const noop = () => {};
const defaultResumeReview: ResumeReviewContextValue = {
  isFullPage: false,
  enter: noop,
  exit: noop,
  queue: null,
  hasEntered: false,
  useLegacyPagination: false,
  go: noop,
  isNavigating: false,
  navigationMethod: null,
  reviewSessionId: null,
  detailSettlement: null,
  reportDetailSettled: noop,
};
// The stage table applies its search through Redux; the UAV reads it from the URL.
function queueInput(
  location: Location,
  currentId: string,
  appliedStageSearch: string | undefined,
): LoadQueueInput {
  const { jobId, stageId } = getApplicantTableScope({ location });
  return {
    location,
    currentId,
    searchQuery: stageId
      ? (appliedStageSearch ?? '')
      : (new URLSearchParams(location.search).get('query') ?? ''),
    stageId,
    funnelId: jobId,
  };
}

const tableQueryKey = (location: Location) =>
  JSON.stringify(buildApplicantsQueryParams(location));

const ResumeReviewContext = createContext<ResumeReviewContextValue | null>(
  null,
);

export function ResumeReviewProvider({ children }: PropsWithChildren) {
  const flagOn = Boolean(
    useSelector(makeSelectWhoami()).feature_flags?.['applicant-resume-review'],
  );
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { search } = location;
  const params = queryString.parse(search);
  const applicantId =
    typeof params.applicantId === 'string' ? params.applicantId : '';
  const isFullPage = flagOn && !mobileDevice && params.resumeReview === '1';
  useEffect(() => {
    if (!isFullPage) return;
    Sentry.setTag('resume_review', true);
    return () => Sentry.setTag('resume_review', undefined);
  }, [isFullPage]);
  const posthog = usePostHog();
  const intl = useIntl();
  const dispatch = useDispatch();
  const store = useStore<{ applicantTableV2: ApplicantTableV2 }>();
  const isFetchingApplicants = useSelector(makeSelectIsFetchingApplicants());
  const hasFetchedApplicants = useSelector(makeSelectHasFetchedApplicants());
  const { job, searchQuery }: ApplicantsV2State = useSelector(
    makeSelectApplicantsV2(),
  );
  const [entry, setEntry] = useState<LoadQueueInput | null>(null);
  const entryRef = useRef<typeof entry>(null);
  const queueAttempted = useRef(false);
  const snapshotRef = useRef<QueueSnapshot | null>(null);
  const [snapshot, setSnapshot] = useState<QueueSnapshot | null>(null);
  const [useLegacyPagination, setUseLegacyPagination] = useState(false);
  const expectedDetailId = useRef<string | null>(null);
  const settledDetailId = useRef<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigation, setNavigation] = useState<{
    targetId: string;
    method: NavigationMethod;
  } | null>(null);
  const previousApplicantId = useRef(applicantId);
  if (previousApplicantId.current !== applicantId) {
    previousApplicantId.current = applicantId;
    if (navigation && navigation.targetId !== applicantId) setNavigation(null);
  }
  const reviewSessionId = useRef<string | null>(null);
  if (isFullPage && !reviewSessionId.current)
    reviewSessionId.current = crypto.randomUUID();
  const activeReview = useRef(false);
  const mounted = useRef(false);
  const [detailSettlement, setDetailSettlement] =
    useState<DetailSettlement | null>(null);
  const tablePageRequest = useRef<object | null>(null);

  if (
    entry &&
    ((snapshot &&
      (!snapshot.ids.includes(applicantId) ||
        isUniversalSearchOpen(location))) ||
      (!snapshot &&
        entry.currentId !== applicantId &&
        !(isFullPage && useLegacyPagination)) ||
      (!isFullPage && useLegacyPagination) ||
      isUniversalSearchOpen(entry.location) !== isUniversalSearchOpen(location))
  ) {
    entryRef.current = null;
    snapshotRef.current = null;
    queueAttempted.current = false;
    setEntry(null);
    setSnapshot(null);
    setUseLegacyPagination(false);
    setNavigation(null);
  }

  const captureEntry = useCallback(() => {
    if (entryRef.current) return;
    const input = queueInput(location, applicantId, searchQuery);
    entryRef.current = input;
    setEntry(input);
  }, [location, applicantId, searchQuery]);

  const captureClose = useCallback(
    (exitMethod: ExitMethod) => {
      if (!activeReview.current) return;
      activeReview.current = false;
      posthog.capture('applicants:resume_review_close', {
        exit_method: exitMethod,
        review_session_id: reviewSessionId.current,
      });
    },
    [posthog],
  );

  const endCapture = useCallback(
    (exitMethod: ExitMethod) => {
      captureClose(exitMethod);
      entryRef.current = null;
      snapshotRef.current = null;
      queueAttempted.current = false;
      expectedDetailId.current = null;
      reviewSessionId.current = null;
      setEntry(null);
      setSnapshot(null);
      setUseLegacyPagination(false);
      setNavigation(null);
      setIsNavigating(false);
    },
    [captureClose],
  );

  // URL restoration starts a session without attributing an invented entry point.
  useEffect(() => {
    if (isFullPage) {
      reviewSessionId.current ??= crypto.randomUUID();
      activeReview.current = true;
      captureEntry();
    } else {
      endCapture(params.persistedPopupType ? 'navigation' : 'drawer_close');
    }
  }, [isFullPage, params.persistedPopupType, endCapture, captureEntry]);

  // The close saga clears the URL before GlobalPopup unmounts this provider.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Strict Mode reconnects effects synchronously without closing the drawer.
      void Promise.resolve().then(() => {
        if (!mounted.current) captureClose('drawer_close');
      });
    };
  }, [captureClose]);

  // The table store settles independently of the drawer's detail request.
  useEffect(() => {
    if (!entry || queueAttempted.current) return;
    const source = resolveProfileViewSource({
      isPagination: false,
      pathname: entry.location.pathname,
      search: entry.location.search,
      accountSlug: entry.location.pathname.split('/')[1],
    });
    const isList =
      !isUniversalSearchOpen(entry.location) &&
      (source === 'pipeline_board' ||
        source === 'applicant_list' ||
        source === 'applicant_search');
    if (isList && (!hasFetchedApplicants || isFetchingApplicants)) return;
    if (source === 'pipeline_board' && job?.id !== entry.funnelId) return;
    const stage = job?.stages.find(stage => stage.id === entry.stageId);
    if (
      source === 'pipeline_board' &&
      entry.stageId !== PIPELINE_CARD_STAGE_ID &&
      !stage
    )
      return;
    let label = intl.formatMessage(messages.universalApplicantView);
    if (
      source === 'pipeline_board' &&
      entry.stageId === PIPELINE_CARD_STAGE_ID
    ) {
      label = intl.formatMessage(openingMessages.pipeline);
    } else if (source === 'pipeline_board' && stage) {
      label = stage.name;
    }
    queueAttempted.current = true;
    const requestEntry = entry;
    void loadQueue({
      ...entry,
      searchQuery: entry.stageId ? (searchQuery ?? '') : entry.searchQuery,
    }).then(result => {
      if (!mounted.current || entryRef.current !== requestEntry) return;
      const fixedQueue = result ? { ...result, label } : null;
      snapshotRef.current = fixedQueue;
      setSnapshot(fixedQueue);
      setUseLegacyPagination(isList && !result);
    });
  }, [
    entry,
    hasFetchedApplicants,
    isFetchingApplicants,
    job,
    searchQuery,
    intl,
  ]);

  const enter = useCallback(
    (entryPoint: EntryPoint) => {
      if (!flagOn || mobileDevice || activeReview.current) return;
      tablePageRequest.current = null;
      reviewSessionId.current ??= crypto.randomUUID();
      activeReview.current = true;
      captureEntry();
      setParamsToUrl({ resumeReview: '1' });
      posthog.capture('applicants:resume_review_open', {
        entry_point: entryPoint,
        review_session_id: reviewSessionId.current,
      });
    },
    [flagOn, mobileDevice, posthog, captureEntry],
  );
  // Full page can reach applicants beyond the table's loaded page, and the
  // drawer's Prev and Next read only that page.
  const showApplicantTablePage = useCallback(() => {
    const requestLocation = history.location;
    const currentId = queryString.parse(requestLocation.search).applicantId;
    const isOnTablePage = (id: string) =>
      Boolean(makeSelectApplicants()(store.getState())[id]);
    if (typeof currentId !== 'string' || !currentId || isOnTablePage(currentId))
      return;
    const request = {};
    tablePageRequest.current = request;
    const tableQuery = tableQueryKey(requestLocation);
    void loadQueue(queueInput(requestLocation, currentId, searchQuery)).then(
      result => {
        if (!mounted.current || tablePageRequest.current !== request) return;
        tablePageRequest.current = null;
        const { location: current } = history;
        const params = queryString.parse(current.search);
        if (
          !result ||
          current.pathname !== requestLocation.pathname ||
          params.applicantId !== currentId ||
          params.resumeReview !== undefined ||
          tableQueryKey(current) !== tableQuery ||
          isOnTablePage(currentId)
        )
          return;
        const perPage = Number(params.per_page) || DEFAULT_ROWS_PER_PAGE;
        setParamsToUrlWithReplace({
          page: Math.floor(result.ids.indexOf(currentId) / perPage) + 1,
        });
      },
    );
  }, [store, searchQuery]);
  const exit = useCallback(
    (exitMethod: ExitMethod) => {
      endCapture(exitMethod);
      removeParamsFromUrl({ resumeReview: '' });
      if (isFullPage) showApplicantTablePage();
    },
    [endCapture, isFullPage, showApplicantTablePage],
  );
  const reportDetailSettled = useCallback(
    (applicantId: string, outcome: ApplicantDetailOutcome) => {
      const currentId = queryString.parse(history.location.search).applicantId;
      if (applicantId !== currentId) return;
      settledDetailId.current = applicantId;
      expectedDetailId.current = null;
      setIsNavigating(false);
      setDetailSettlement({ applicantId, outcome });
    },
    [],
  );
  const go = useCallback(
    (direction: 'next' | 'previous', method: NavigationMethod) => {
      if (!flagOn || mobileDevice || expectedDetailId.current) return;
      const currentId = queryString.parse(history.location.search).applicantId;
      if (
        typeof currentId !== 'string' ||
        settledDetailId.current !== currentId
      )
        return;
      const fixedQueue = snapshotRef.current;
      const position = fixedQueue?.ids.indexOf(currentId) ?? -1;
      if (!fixedQueue || position < 0) return;
      const targetId =
        fixedQueue.ids[position + (direction === 'next' ? 1 : -1)];
      if (!targetId) return;
      expectedDetailId.current = targetId;
      settledDetailId.current = null;
      setIsNavigating(true);
      setNavigation({ targetId, method });
      setParamsToUrl({ applicantId: targetId });
      dispatch(setApplicantMessagesAction(currentId, []));
    },
    [flagOn, mobileDevice, dispatch],
  );
  const position = snapshot?.ids.indexOf(applicantId) ?? -1;
  const currentReviewSessionId = isFullPage ? reviewSessionId.current : null;
  const navigationMethod =
    navigation?.targetId === applicantId ? navigation.method : null;
  const hasEntered = isFullPage && entry !== null;
  const queue = useMemo(
    () =>
      hasEntered && snapshot && position >= 0
        ? { ...snapshot, position }
        : null,
    [hasEntered, snapshot, position],
  );
  useEffect(() => {
    if (!isFullPage || !queue) return;
    return addApplicantTransitionedListener(event => {
      if (event.detail.applicantId === applicantId) go('next', 'auto_advance');
    });
  }, [isFullPage, queue, applicantId, go]);
  const value = useMemo(
    () => ({
      isFullPage,
      enter,
      exit,
      hasEntered,
      useLegacyPagination,
      queue,
      go,
      isNavigating,
      navigationMethod,
      reviewSessionId: currentReviewSessionId,
      detailSettlement,
      reportDetailSettled,
    }),
    [
      isFullPage,
      enter,
      exit,
      hasEntered,
      useLegacyPagination,
      queue,
      go,
      isNavigating,
      navigationMethod,
      currentReviewSessionId,
      detailSettlement,
      reportDetailSettled,
    ],
  );

  return (
    <ResumeReviewContext.Provider value={value}>
      {children}
    </ResumeReviewContext.Provider>
  );
}

export function useResumeReview() {
  return useContext(ResumeReviewContext) ?? defaultResumeReview;
}

export function useOptionalResumeReview() {
  return useContext(ResumeReviewContext);
}
